import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField
} from '@mui/material';
import { FlowBgKindToggle, ImageFileDropZone } from './flow/components/flowBgAssetsUi';

/**
 * @param {'background' | 'location'} props.mode
 * @param {object | null} props.record — editing doc or null for create
 */
const BackgroundForm = ({ open, onClose, onSubmit, record, isEdit, mode = 'background' }) => {
  const isLocation = mode === 'location';
  const [kind, setKind] = useState('background');
  const [label, setLabel] = useState('');
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (record && isEdit) {
      if (isLocation) {
        setLabel(String(record.label || '').trim());
      } else {
        setKind(record.kind === 'slide' ? 'slide' : 'background');
      }
    } else {
      setKind('background');
      setLabel('');
    }
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }, [record, isEdit, open, isLocation]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const chosen = file || fileRef.current?.files?.[0] || null;
    if (!isEdit && !chosen) {
      return;
    }
    if (isLocation) {
      const trimmed = String(label || '').trim();
      if (!trimmed) {
        return;
      }
      onSubmit({ label: trimmed, file: chosen });
    } else {
      onSubmit({ kind, file: chosen });
    }
  };

  const handleClose = () => {
    setKind('background');
    setLabel('');
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
    onClose();
  };

  const title = isLocation
    ? isEdit
      ? 'Edit location'
      : 'Add location'
    : isEdit
      ? 'Edit background image'
      : 'Upload background image';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {isEdit && record?.thumbnailUrl ? (
              <Box
                component="img"
                src={record.thumbnailUrl || record.imageUrl}
                alt=""
                sx={{ maxHeight: 160, width: '100%', objectFit: 'contain', borderRadius: 1, bgcolor: 'action.hover' }}
              />
            ) : null}
            {isLocation ? (
              <TextField
                label="Location name"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
                fullWidth
                autoFocus={!isEdit}
                inputProps={{ maxLength: 200 }}
              />
            ) : (
              <FlowBgKindToggle value={kind} onChange={setKind} />
            )}
            <ImageFileDropZone file={file} onFile={setFile} inputRef={fileRef} />
            {!isEdit ? (
              <Typography variant="caption" color="text.secondary">
                Image is uploaded to R2. Required for new {isLocation ? 'locations' : 'backgrounds'}.
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Replace file only if you need a new image; you can change {isLocation ? 'the name' : 'type'} and save
                without a new file.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {isEdit ? 'Save' : 'Upload'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BackgroundForm;
